<script setup lang="ts">
/**
 * BaseChip — 토글 칩 원자(필터·선택 분류). 활성/비활성 2상태.
 *  active=true → navy 채움(선택), false → 흰색(+hover blue).
 * 클래스는 chipStyles.ts SSoT 상수 경유(MaterialsPage 등과 드리프트 방지).
 * 라벨은 label prop 또는 기본 slot. aria-pressed 는 active 로 자동 바인딩.
 */
import { cva } from 'class-variance-authority';
import { computed } from 'vue';

import { CHIP_ACTIVE_CLASS, CHIP_BASE_CLASS, CHIP_INACTIVE_CLASS } from '#/lib/chipStyles';
import { cn } from '#/lib/utils';

const chipVariants = cva(CHIP_BASE_CLASS, {
  variants: {
    active: {
      true: CHIP_ACTIVE_CLASS,
      false: CHIP_INACTIVE_CLASS,
    },
  },
  defaultVariants: {
    active: false,
  },
});

interface Props {
  /** 활성(선택) 여부 — 시각 상태·aria-pressed. */
  active?: boolean;
  /** 표시 라벨(미지정 시 기본 slot 사용). */
  label?: string;
}

const props = withDefaults(defineProps<Props>(), {
  active: false,
  label: undefined,
});

defineEmits<{
  /** 칩 클릭 — 부모가 토글 처리. */
  click: [event: MouseEvent];
}>();

const classes = computed(() => cn(chipVariants({ active: props.active })));
</script>

<template>
  <button type="button" :class="classes" :aria-pressed="active" @click="$emit('click', $event)">
    <slot>{{ label }}</slot>
  </button>
</template>
